/**
 * The height a state that owns the whole screen should fill: the viewport
 * minus whatever chrome the surrounding layout declared through
 * `--app-chrome-offset` (`src/styles/index.css`). Used by `LinkStatusCard`'s
 * `fullScreen` mode and by the loading states that precede those screens, so
 * neither jumps when one replaces the other.
 *
 * Sized against the viewport rather than the ancestor chain on purpose:
 * `AppLayout`'s shell is `min-h-screen` with an `auto` height, so
 * `<main className="flex-1">` is *stretched* by flex layout but never gets a
 * *definite* height - a percentage height inside it therefore resolves to
 * `auto` and collapses to content height, leaving `items-center` no spare
 * space to center within. Measured in Chromium at 1440x900 on the blocked
 * notice before this existed: `main` 820px tall but the `h-full` box inside
 * it only 651px (exactly the card's own content height), leaving 64px above
 * the card and 233px below it. With this: 148.5px on both sides.
 *
 * The offset is *inherited*, never passed in, which is what lets one class
 * serve a state mounted in more than one place: `AccessDeniedState` renders
 * both inside `AppLayout` (offset = the navbar) and, from the admin console's
 * route guard, with no shell at all (offset = the `0px` root default). See the
 * `--app-chrome-offset` comment for why no fixed navbar-relative constant
 * could be correct in both.
 *
 * `dvh` (not `vh`) so mobile browser chrome does not push the content below
 * the fold.
 */
export const FULL_SCREEN_STATE_MIN_H = 'min-h-[calc(100dvh-var(--app-chrome-offset))]';

/**
 * The id every shell gives its `<main>` landmark. It is the skip-navigation
 * target and, in the admin console (and `AppLayout`'s own scroll container),
 * the element that scrolls instead of the window - so `ScrollToTop` resets it
 * alongside the window.
 */
export const MAIN_CONTENT_ID = 'main-content';
